import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save } from "lucide-react";

import {
  type NotificationCode,
  type NotificationLogEntry,
  type NotificationSetting,
  notificationsApi,
} from "@/api/notifications";
import { useMe } from "@/hooks/useMe";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

const CODES: NotificationCode[] = ["N1", "N2", "N3", "N4", "N5", "N6", "N7", "N8"];

interface Draft {
  code: NotificationCode;
  is_enabled: boolean;
  params_json: string;
  invalid: boolean;
}

function toDraft(s: NotificationSetting): Draft {
  return {
    code: s.code,
    is_enabled: s.is_enabled,
    params_json: JSON.stringify(s.params, null, 2),
    invalid: false,
  };
}

type Tab = "settings" | "log";

export function NotificationsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("settings");

  const isAdminPlus = me.data?.role === "admin" || me.data?.role === "super_admin";

  const settings = useQuery({
    queryKey: ["notification-settings"],
    queryFn: () => notificationsApi.list(),
    enabled: isAdminPlus && tab === "settings",
  });

  const [drafts, setDrafts] = useState<Record<string, Draft>>({});

  useEffect(() => {
    if (!settings.data) return;
    const by: Record<string, Draft> = {};
    for (const s of settings.data) by[s.code] = toDraft(s);
    setDrafts(by);
  }, [settings.data]);

  const dirty = useMemo(() => {
    if (!settings.data) return false;
    return settings.data.some((s) => {
      const d = drafts[s.code];
      if (!d) return false;
      if (d.is_enabled !== s.is_enabled) return true;
      try {
        return JSON.stringify(JSON.parse(d.params_json)) !== JSON.stringify(s.params);
      } catch {
        return true;
      }
    });
  }, [drafts, settings.data]);

  const save = useMutation({
    mutationFn: () => {
      const items: NotificationSetting[] = CODES.map((c) => {
        const d = drafts[c];
        return {
          code: c,
          is_enabled: d?.is_enabled ?? true,
          params: d ? JSON.parse(d.params_json) : {},
        };
      });
      return notificationsApi.upsert(items);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["notification-settings"] });
    },
  });

  const setDraft = (code: NotificationCode, patch: Partial<Draft>) => {
    setDrafts((prev) => ({
      ...prev,
      [code]: { ...(prev[code] ?? toDraft({ code, is_enabled: true, params: {} })), ...patch },
    }));
  };

  const validateAndSave = () => {
    let anyInvalid = false;
    const next: Record<string, Draft> = {};
    for (const c of CODES) {
      const d = drafts[c];
      if (!d) continue;
      let invalid = false;
      try {
        JSON.parse(d.params_json);
      } catch {
        invalid = true;
        anyInvalid = true;
      }
      next[c] = { ...d, invalid };
    }
    setDrafts(next);
    if (!anyInvalid) save.mutate();
  };

  if (!isAdminPlus) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-slate-500">
          {t("common.read_only")}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t("notifications.title")}</CardTitle>
              <CardDescription>{t("notifications.subtitle")}</CardDescription>
            </div>
            {tab === "settings" && (
              <Button onClick={validateAndSave} disabled={!dirty || save.isPending}>
                <Save className="h-4 w-4" />
                {save.isPending ? t("common.saving") : t("common.save")}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1 w-fit">
            {(["settings", "log"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setTab(m)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm transition-colors",
                  tab === m
                    ? "bg-white shadow-sm font-medium"
                    : "text-slate-600 hover:text-slate-900",
                )}
              >
                {t(`notifications.tab_${m}`)}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {tab === "log" ? (
        <NotificationLogTab />
      ) : settings.isLoading ? (
        <Card>
          <CardContent className="p-6 text-sm text-slate-500">{t("common.loading")}</CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {CODES.map((code) => {
            const d = drafts[code];
            return (
              <Card key={code}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">
                      <span className="font-mono">{code}</span>{" "}
                      <span className="text-slate-600">— {t(`notifications.codes.${code}`)}</span>
                    </CardTitle>
                    <ToggleSwitch
                      checked={d?.is_enabled ?? true}
                      onChange={(v) => setDraft(code, { is_enabled: v })}
                    />
                  </div>
                  <CardDescription>{t(`notifications.descriptions.${code}`)}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-1">
                    <Label>{t("notifications.params")}</Label>
                    <textarea
                      value={d?.params_json ?? "{}"}
                      onChange={(e) => setDraft(code, { params_json: e.target.value, invalid: false })}
                      className={cn(
                        "w-full rounded-md border px-3 py-2 text-xs font-mono min-h-[120px]",
                        d?.invalid
                          ? "border-red-400 bg-red-50"
                          : "border-slate-300 bg-white",
                      )}
                      spellCheck={false}
                    />
                    {d?.invalid && (
                      <div className="text-xs text-red-600">{t("notifications.invalid_json")}</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ToggleSwitch({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={cn(
        "relative inline-flex h-6 w-11 items-center rounded-full transition-colors",
        checked ? "bg-emerald-500" : "bg-slate-300",
      )}
      aria-pressed={checked}
    >
      <span
        className={cn(
          "inline-block h-5 w-5 transform rounded-full bg-white transition-transform",
          checked ? "translate-x-5" : "translate-x-0.5",
        )}
      />
    </button>
  );
}

function NotificationLogTab() {
  const { t } = useTranslation();
  const [filterCode, setFilterCode] = useState<NotificationCode | "">("");
  const log = useQuery({
    queryKey: ["notification-log", filterCode],
    queryFn: () => notificationsApi.log(filterCode ? { code: filterCode } : {}),
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="p-3 flex items-center gap-3">
          <Label>{t("notifications.filter_code")}</Label>
          <select
            value={filterCode}
            onChange={(e) => setFilterCode(e.target.value as NotificationCode | "")}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm"
          >
            <option value="">{t("notifications.filter_all")}</option>
            {CODES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {log.isLoading ? (
            <div className="p-6 text-sm text-slate-500">{t("common.loading")}</div>
          ) : (log.data ?? []).length === 0 ? (
            <div className="p-6 text-sm text-slate-500">{t("notifications.log_empty")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-slate-500">
                    <th className="p-2">{t("audit.ts")}</th>
                    <th className="p-2">{t("audit.action")}</th>
                    <th className="p-2 text-right">{t("notifications.col_sent")}</th>
                    <th className="p-2 text-right">{t("notifications.col_failed")}</th>
                    <th className="p-2 text-right">{t("notifications.col_skipped")}</th>
                    <th className="p-2">{t("notifications.col_payload")}</th>
                  </tr>
                </thead>
                <tbody>
                  {log.data!.map((e: NotificationLogEntry) => (
                    <tr key={e.id} className="border-t border-slate-100">
                      <td className="p-2 text-xs text-slate-500 whitespace-nowrap">
                        {new Date(e.ts).toLocaleString("uk-UA", { timeZone: "Europe/Kyiv" })}
                      </td>
                      <td className="p-2">
                        <span className="font-mono">{e.code}</span>
                        {!e.is_enabled && (
                          <span className="ml-1 text-xs text-slate-400">(disabled)</span>
                        )}
                      </td>
                      <td className="p-2 text-right font-mono text-emerald-700">{e.sent}</td>
                      <td
                        className={cn(
                          "p-2 text-right font-mono",
                          e.failed > 0 ? "text-red-700 font-semibold" : "text-slate-400",
                        )}
                      >
                        {e.failed}
                      </td>
                      <td className="p-2 text-right font-mono text-slate-400">{e.skipped}</td>
                      <td className="p-2 text-xs text-slate-500 max-w-md truncate" title={JSON.stringify(e.payload)}>
                        {e.payload ? JSON.stringify(e.payload) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
